import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useAuth } from '../stores/auth';
import {
  ArrowLeft,
  CircleDot,
  CornerDownRight,
  Monitor,
  ShoppingCart,
  Refrigerator,
  CreditCard,
  MoreHorizontal,
  Shuffle,
  Camera,
  Check,
} from 'lucide-react';

const EXTRA_POINT_TYPES = [
  { value: 'ISLAND', label: 'Ilha', icon: CircleDot, color: 'bg-blue-100', iconColor: 'text-blue-600' },
  { value: 'END_CAP', label: 'Ponta de Gondola', icon: CornerDownRight, color: 'bg-purple-100', iconColor: 'text-purple-600' },
  { value: 'DISPLAY', label: 'Display', icon: Monitor, color: 'bg-orange-100', iconColor: 'text-orange-600' },
  { value: 'CROSS_MERCH', label: 'Cross Merchandising', icon: Shuffle, color: 'bg-teal-100', iconColor: 'text-teal-600' },
  { value: 'COOLER', label: 'Geladeira', icon: Refrigerator, color: 'bg-cyan-100', iconColor: 'text-cyan-600' },
  { value: 'CHECKOUT', label: 'Checkout', icon: CreditCard, color: 'bg-pink-100', iconColor: 'text-pink-600' },
  { value: 'OTHER', label: 'Outro', icon: MoreHorizontal, color: 'bg-gray-100', iconColor: 'text-gray-600' },
] as const;

const DURATION_OPTIONS = [
  { value: 'PERMANENT', label: 'Permanente' },
  { value: 'TEMPORARY', label: 'Temporario' },
  { value: 'SEASONAL', label: 'Sazonal' },
] as const;

interface Brand {
  id: string;
  name: string;
}

export default function ExtraPoint({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const [selectedType, setSelectedType] = useState<string>('');
  const [duration, setDuration] = useState<string>('');
  const [brandId, setBrandId] = useState<string>('');
  const [locationInStore, setLocationInStore] = useState('');
  const [notes, setNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch brands
  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ['brands'],
    queryFn: () => api.get('/products/brands').then(res => res.data.data),
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/extra-points', {
        visit_id: visitId,
        store_id: storeId,
        promoter_id: user?.id, // backend should override from visit
        type: selectedType,
        duration,
        brand_id: brandId || undefined,
        location_in_store: locationInStore,
        notes: notes || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      setSuccessMessage('Ponto extra registrado com sucesso!');
      // Reset form for next entry
      setSelectedType('');
      setDuration('');
      setBrandId('');
      setLocationInStore('');
      setNotes('');
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || 'Erro ao registrar ponto extra');
    },
  });

  const handleSubmit = () => {
    if (!selectedType) {
      alert('Selecione o tipo de ponto extra');
      return;
    }
    if (!duration) {
      alert('Selecione a duracao');
      return;
    }
    submitMutation.mutate();
  };

  const handlePhotoCapture = () => {
    setLocation(`/camera/${visitId}?type=EXTRA_POINT`);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation(`/visit/${storeId}`)}
            className="p-2 -ml-2 rounded-full hover:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Registrar Ponto Extra</h1>
          </div>
        </div>
      </header>

      {/* Success Toast */}
      {successMessage && (
        <div className="mx-4 mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 p-4 space-y-6 pb-8">
        {/* Type Selection */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Tipo de Ponto Extra</h3>
          <div className="grid grid-cols-3 gap-3">
            {EXTRA_POINT_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = selectedType === type.value;
              return (
                <button
                  key={type.value}
                  onClick={() => setSelectedType(type.value)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className={`p-2 rounded-full ${type.color}`}>
                    <Icon className={`h-5 w-5 ${type.iconColor}`} />
                  </div>
                  <span className={`text-xs font-medium text-center leading-tight ${
                    isSelected ? 'text-primary' : 'text-gray-700'
                  }`}>
                    {type.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Duration Selection */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Duracao</h3>
          <div className="flex gap-3">
            {DURATION_OPTIONS.map((option) => {
              const isSelected = duration === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setDuration(option.value)}
                  className={`flex-1 py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                    isSelected
                      ? 'border-primary bg-primary text-white'
                      : 'border-gray-200 bg-white text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Brand Select */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Marca (opcional)</h3>
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">Selecione uma marca</option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        {/* Location in Store */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Local na Loja</h3>
          <input
            type="text"
            value={locationInStore}
            onChange={(e) => setLocationInStore(e.target.value)}
            placeholder="Ex: Corredor 3, Secao Bebidas"
            className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Notes */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Observacoes (opcional)</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione observacoes sobre o ponto extra..."
            rows={3}
            className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Photo Capture */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Foto</h3>
          <button
            onClick={handlePhotoCapture}
            className="w-full flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-300 bg-white"
          >
            <Camera className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-500">Tirar foto do ponto extra</span>
          </button>
          <p className="text-xs text-gray-400 mt-1">A foto será vinculada à visita automaticamente</p>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={submitMutation.isPending || !selectedType || !duration}
          className="w-full flex items-center justify-center gap-2 p-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Registrando...</span>
            </>
          ) : (
            <span>Registrar Ponto Extra</span>
          )}
        </button>
      </main>
    </div>
  );
}
